// Iter 76 — Vendor Payments cross-vendor list page.
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    MagnifyingGlass, CurrencyInr, CaretLeft, CaretRight, ArrowRight,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { formatAmount, PAYMENT_MODES } from "./vendor_constants";

const PAGE_SIZE = 25;

export default function VendorPaymentsList() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();

    const q = params.get("q") || "";
    const vendorId = params.get("vendor_id") || "";
    const mode = params.get("payment_mode") || "";
    const sort = params.get("sort") || "recently_added";
    const page = Math.max(1, parseInt(params.get("page") || "1", 10));

    const [searchInput, setSearchInput] = useState(q);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [vendors, setVendors] = useState([]);

    const updateParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (!value) next.delete(key); else next.set(key, value);
        if (key !== "page") next.delete("page");
        setParams(next, { replace: false });
    };

    useEffect(() => {
        let cancelled = false;
        api.get("/vendor-payments", {
            params: {
                q: q || undefined,
                vendor_id: vendorId || undefined,
                payment_mode: mode || undefined,
                sort,
                page,
                page_size: PAGE_SIZE,
            },
        })
            .then(({ data }) => {
                if (cancelled) return;
                setRows(data.items || []);
                setTotal(data.total || 0);
                setPages(data.pages || 0);
            })
            .catch((err) => { if (!cancelled) toast.error(formatApiError(err, "Failed to load payments")); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [q, vendorId, mode, sort, page]);

    useEffect(() => {
        let cancelled = false;
        api.get("/vendors", { params: { page_size: 200, sort: "name_asc" } })
            .then(({ data }) => { if (!cancelled) setVendors(data.items || []); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const t = setTimeout(() => { if (searchInput !== q) updateParam("q", searchInput); }, 300);
        return () => clearTimeout(t);
    }, [searchInput, q]);

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1400px]" data-testid="vpay-list-page">
            <PageHeader
                eyebrow="Vendor Management"
                title="Vendor Payments"
                description="Cross-vendor view of payments recorded against vendor bills."
            />

            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="vpay-filter-row">
                <div className="relative flex-1 min-w-[240px] max-w-md">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input data-testid="vpay-search" placeholder="Search code, vendor, bill #, reference…"
                        value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="pl-9" />
                </div>
                <select data-testid="vpay-filter-vendor" value={vendorId}
                    onChange={(e) => updateParam("vendor_id", e.target.value)}
                    className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white">
                    <option value="">All vendors</option>
                    {vendors.map((v) => (<option key={v.id} value={v.id}>{v.code} · {v.name}</option>))}
                </select>
                <select data-testid="vpay-filter-mode" value={mode}
                    onChange={(e) => updateParam("payment_mode", e.target.value)}
                    className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white">
                    <option value="">All modes</option>
                    {PAYMENT_MODES.map((m) => (<option key={m} value={m}>{m}</option>))}
                </select>
                <select data-testid="vpay-sort" value={sort}
                    onChange={(e) => updateParam("sort", e.target.value)}
                    className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white ml-auto">
                    <option value="recently_added">Recently added</option>
                    <option value="payment_date_desc">Payment date ↓</option>
                    <option value="payment_date_asc">Payment date ↑</option>
                    <option value="amount_desc">Amount ↓</option>
                    <option value="amount_asc">Amount ↑</option>
                </select>
            </div>

            <div className="mt-5 rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="vpay-table">
                        <thead className="bg-slate-50 text-slate-600">
                            <tr>
                                <th className="text-left font-medium px-4 py-3">Code</th>
                                <th className="text-left font-medium px-4 py-3">Date</th>
                                <th className="text-left font-medium px-4 py-3">Vendor</th>
                                <th className="text-left font-medium px-4 py-3">Bill</th>
                                <th className="text-left font-medium px-4 py-3">Mode</th>
                                <th className="text-left font-medium px-4 py-3">Reference</th>
                                <th className="text-right font-medium px-4 py-3">Amount</th>
                                <th className="w-10" />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={8} className="text-center text-slate-400 py-12">Loading…</td></tr>}
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={8} className="py-16">
                                    <EmptyState icon={<CurrencyInr size={32} className="text-slate-400" />}
                                        title="No payments" message="Record payments from a Vendor Bill's detail page." />
                                </td></tr>
                            )}
                            {!loading && rows.map((p) => (
                                <tr key={p.id} data-testid={`vpay-row-${p.code}`}
                                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                    onClick={() => navigate(`/vendor-bills/${p.vendor_bill_id}`)}>
                                    <td className="px-4 py-3 font-mono text-xs">{p.code}</td>
                                    <td className="px-4 py-3 text-xs text-slate-700">{formatDate(p.payment_date)}</td>
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-slate-900">{p.vendor_name}</div>
                                        <div className="text-xs text-slate-500 font-mono">{p.vendor_code}</div>
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        <div className="font-mono text-slate-700">{p.vendor_bill_code}</div>
                                        <div className="text-slate-500 text-[11px]">{p.vendor_bill_number}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <Badge variant="secondary" className="bg-slate-100 text-slate-700 hover:bg-slate-100">{p.payment_mode}</Badge>
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono text-slate-600">{p.reference_number || "—"}</td>
                                    <td className="px-4 py-3 text-right font-mono text-slate-900">{formatAmount(p.amount, p.currency)}</td>
                                    <td className="px-4 py-3 text-right"><ArrowRight size={14} className="text-slate-400" /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {!loading && total > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/50 text-xs text-slate-600">
                        <div data-testid="vpay-pagination-summary">
                            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button data-testid="vpay-page-prev" size="sm" variant="outline"
                                disabled={page <= 1} onClick={() => updateParam("page", page - 1)}><CaretLeft size={14} /></Button>
                            <span className="px-2">Page {page} of {pages}</span>
                            <Button data-testid="vpay-page-next" size="sm" variant="outline"
                                disabled={page >= pages} onClick={() => updateParam("page", page + 1)}><CaretRight size={14} /></Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
